import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { logError } from '../../lib/error-logger';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    logError({ type: 'render', error, metadata: { componentStack: errorInfo.componentStack } });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="bg-red-50 border border-red-100 rounded-[2rem] p-12 text-center max-w-lg">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-xl font-black text-ink uppercase tracking-tight mb-2">{i18next.t('errorBoundary.title')}</h2>
            <p className="text-[12px] font-bold text-gray-400 uppercase tracking-widest leading-relaxed mb-8">
              {i18next.t('errorBoundary.description')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-4 bg-black text-white rounded-2xl text-[12px] font-black uppercase tracking-widest shadow-xl hover:bg-zinc-800 transition-all"
            >
              {i18next.t('errorBoundary.reload')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
